import * as React from "react"
import { ListItemIcon, Menu, MenuItem } from "@material-ui/core"
import { FC } from "react"
import AddCardIcon from '@mui/icons-material/AddCard';
import Widgets from '@material-ui/icons/Widgets';
import ShoppingCartCheckoutIcon from '@mui/icons-material/ShoppingCartCheckout';
import { useTranslations } from "../../../store/translations/hooks";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { TK } from "../../../store/translations/translationKeys";
import { getPackAsync, requestAppointmentMbPaymentAsync, requestAppointmentMbwayPaymentAsync, requestPackMbPaymentAsync, requestPackMbwayPaymentAsync, sendAppointmentCheckoutEmailAsync, sendPackCheckoutEmailAsync } from "../../../ajax/requests";
import { useDispatch, useSelector } from "react-redux";
import { alertError, alertGenericError, alertSuccess } from "../../../store/alerts/actions";
import { AppointmentData, ClientPack, FeatureEnum, Payment, PaymentStatus } from "../../../models";
import { Guid } from "../../../shared/utils";
import { useGeneralSettings, usePackSettings } from "../../../store/settings/hooks";
import { AutocompleteTextField } from "./AutocompleteTextField";
import { MultiplePaymentsField } from "./MultiplePaymentsField";
import { getMyUser, hasFeature, isAdministrator } from "../../../store/session/selectors";
import { formatEuros } from "../../../shared/string";
import { useEntity } from "../../../store/entities/hooks";
import { getActualPriceWithPack } from "../../../shared/appointmint/appointment";
import Typography from "../../Typography";
import { getSettings } from "../../../store/settings/selectors";
import { Selectors as GeneralSelectors, Actions as GeneralActions } from "../../../store/general";
import AppButton from '../../AppButton';
import { PaymentLabel } from "./PaymentsList";


export const getPackDesc = (clientName?: string, pack?: ClientPack): string|undefined => {
    if(!pack) return undefined
    return `${pack.packName} - ${(pack.validityFrom ?? pack.creationDate)?.substring(0, 10)}` +
        (clientName !== pack.clientName ? ` (${pack.clientName})` : '')
}

export interface AvailablePackOption {
    pack: ClientPack
    disabled: boolean
    disabledReason?: string
}

export const PaymentsButtons: FC<{
    entity: ClientPack | AppointmentData
    totalToPay: number
    totalPaid: number
    showManual?: boolean
    showAutomatic?: boolean
    showPacks?: boolean
    openPaymentModal?: boolean
    showMissingValue?: boolean
    availablePacks?: AvailablePackOption[]
    onPaymentsChanged: (payments: Payment[]) => void
    onPaymentAdded: (payment: Payment) => void
    onPackAdded?: (pack: ClientPack | undefined) => void
}> = ({
    entity,
    totalToPay,
    totalPaid,
    showManual,
    showAutomatic,
    showPacks,
    openPaymentModal,
    availablePacks,
    onPaymentAdded,
    onPackAdded,
    onPaymentsChanged,
}) => {
    const t = useTranslations()
    const dispatch = useDispatch()
    const generalSettings = useGeneralSettings()
    const user = useSelector(getMyUser)
    const isAdmin = useSelector(isAdministrator)
    const useProfessionalPacks = useSelector(hasFeature(FeatureEnum.ProfessionalPacks))
    const automaticPaymentsEnabled = useSelector(hasFeature(FeatureEnum.AutomaticPayments))
    const packData = useEntity<ClientPack>('clientPacks', 'packId' in entity ? entity.packId ?? 'none' : 'none')
    const packSettings = usePackSettings(packData?.packTemplateId ?? '', packData?.packVersion ?? 0)

    const settings = useSelector(getSettings)
    const selectedCashRegisterName = useSelector(GeneralSelectors.getSelectedCashRegisterName)

    const sharedRegisterNames = settings.cashRegisterSettings?.enabled
        ? (settings.cashRegisterSettings?.sharedRegisterNames ?? [])
        : []
    const needsRegisterSelection = sharedRegisterNames.length > 0 && selectedCashRegisterName === undefined

    const [automaticPaymentLoading, setAutomaticPaymentLoading] = React.useState(false)
    const [packMenuAnchor, setPackMenuAnchor] = React.useState<HTMLElement | null>(null)
    const [addPaymentAnchor, setAddPaymentAnchor] = React.useState<HTMLElement | null>(null)
    const [requestMenuAnchor, setRequestMenuAnchor] = React.useState<HTMLElement | null>(null)
    const [mbwayModal, setMbwayModal] = React.useState(false)
    const [paymentModal, setPaymentModal] = React.useState(openPaymentModal || false)
    const [manualPack, setManualPack] = React.useState<ClientPack | undefined>()
    const [registerModalOpen, setRegisterModalOpen] = React.useState(false)
    const [pendingAction, setPendingAction] = React.useState<((name: string) => void) | null>(null)

    const withRegisterCheck = (action: (cashRegisterName: string) => void) => {
        if (needsRegisterSelection) {
            setPendingAction(() => action)
            setRegisterModalOpen(true)
        } else {
            action(selectedCashRegisterName ?? '')
        }
    }

    const handleRegisterSelect = (name: string) => {
        dispatch(GeneralActions.selectCashRegisterName(name))
        setRegisterModalOpen(false)
        pendingAction?.(name)
        setPendingAction(null)
    }

    const entityType: 'pack'|'appointment' = 'packTemplateId' in entity ? 'pack' : 'appointment'

    const isProfessional = !!user?.professionalId
    const packIsMandatory = entityType === 'appointment' && useProfessionalPacks && isProfessional
    const missingPayment = totalToPay - totalPaid > 0

    const packOptions = React.useMemo<AvailablePackOption[]>(() => {
        const manualOption = manualPack
            ? [{
                pack: manualPack,
                disabled: false,
                disabledReason: undefined,
            }]
            : [];
        const existingPackIds = new Set((availablePacks ?? []).map(option => option.pack.id));

        return (manualOption as AvailablePackOption[])
            .filter(option => !existingPackIds.has(option.pack.id))
            .concat(availablePacks ?? []);
    }, [availablePacks, manualPack]);

    const packOptionLabelMap = React.useMemo(
        () => Object.fromEntries(packOptions.map(option => {
            const date = (option.pack.validityFrom ?? option.pack.creationDate)?.substring(0, 10);
            const baseLabel = date ? `${option.pack.packName} - ${date}` : option.pack.packName;
            const label = option.disabled && option.disabledReason
                ? `${baseLabel} - ${option.disabledReason}`
                : baseLabel;

            return [label, option];
        })),
        [packOptions],
    );
    const packOptionLabels = React.useMemo(() => Object.keys(packOptionLabelMap), [packOptionLabelMap]);

    const handleQuickPayment = (paymentType: string) => {
        setAddPaymentAnchor(null)
        withRegisterCheck((cashRegisterName) => {
            const payment: any = {
                id: Guid.newGuid(),
                date: new Date().toISOString(),
                type: paymentType,
                value: Math.max(0, totalToPay - totalPaid),
                status: PaymentStatus.Manual,
            }
            if (cashRegisterName) payment.cashRegisterName = cashRegisterName
            onPaymentAdded(payment as Payment)
        })
    }

    const handleQuickPack = (pack: ClientPack) => () => {
        onPackAdded && onPackAdded(pack)
        setPackMenuAnchor(null)
    }

    const handleSendCheckoutEmail = () => {
        setRequestMenuAnchor(null)
        setAutomaticPaymentLoading(true)
        const request = entityType === 'pack' ? sendPackCheckoutEmailAsync : sendAppointmentCheckoutEmailAsync
        request(entity.id)
            .then(() => dispatch(alertSuccess(t(TK.sent))))
            .catch(error => {
                if ('data' in error && 'message' in error.data) {
                    dispatch(alertError(error.data.message))
                } else {
                    dispatch(alertGenericError())
                }
            })
            .finally(() => setAutomaticPaymentLoading(false))
    }

    const handleAutomaticPayment = (type: 'mb'|'mbw') => () => {
        setAutomaticPaymentLoading(true)
        setMbwayModal(false)

        const request = type === 'mbw'
            ? (entityType === 'pack' ? requestPackMbwayPaymentAsync : requestAppointmentMbwayPaymentAsync)
            : (entityType === 'pack' ? requestPackMbPaymentAsync : requestAppointmentMbPaymentAsync)

        request(entity.id)
            .then(({paymentId, paymentNotes}) => {
                dispatch(alertSuccess(t(TK.sent)))
                onPaymentAdded({
                    date: new Date().toISOString(),
                    type: `auto-${type}`,
                    value: totalToPay - totalPaid,
                    id: paymentId ?? Guid.newGuid(),
                    status: PaymentStatus.InProgress,
                    notes: paymentNotes,
                })
            })
            .catch(error => {
                if ('data' in error && 'message' in error.data) {
                    dispatch(alertError(error.data.message))
                } else {
                    dispatch(alertGenericError())
                }
            })
            .finally(() => setAutomaticPaymentLoading(false))
    }

    const onPackChanged = (packDesc: string) => {
        const option = packOptionLabelMap[packDesc]
        if (option?.disabled) return
        onPackAdded && onPackAdded(option?.pack)
    }

    const onAddManualPack = async (packId: string) => {
        if (!onPackAdded) return
        const pack = await getPackAsync(packId)
        setManualPack(pack)
        onPackAdded(pack)
    }

    const showPackButton = showPacks && !!packOptions?.length && (missingPayment || useProfessionalPacks)
    const showAddPaymentButton = showManual && !packIsMandatory && (missingPayment || useProfessionalPacks)
    const showRequestButton = showAutomatic && missingPayment && automaticPaymentsEnabled && !packIsMandatory

    return (<>

        {showPackButton && (
            <AppButton
                color="info"
                size="lg"
                style={{width: '14rem'}}
                outline
                onClick={packOptions.length === 1
                    ? handleQuickPack(packOptions[0].pack)
                    : (e: React.MouseEvent<HTMLElement>) => setPackMenuAnchor(e.currentTarget)
                }
            >
                <Widgets fontSize="small"/>{t(TK.addPack)}
            </AppButton>
        )}

        {showAddPaymentButton && (
            <AppButton
                color="info"
                size="lg"
                style={{width: '14rem'}}
                outline
                onClick={(e: React.MouseEvent<HTMLElement>) => setAddPaymentAnchor(e.currentTarget)}
            >
                <AddCardIcon fontSize="small"/>{t(TK.registerPayment)}
            </AppButton>
        )}

        {showRequestButton && (
            <AppButton
                color="info"
                size="lg"
                style={{width: '14rem'}}
                outline
                onClick={(e: React.MouseEvent<HTMLElement>) => setRequestMenuAnchor(e.currentTarget)}
                disabled={automaticPaymentLoading}
            >
                <ShoppingCartCheckoutIcon fontSize="small"/>{t(TK.requestPayment)}
            </AppButton>
        )}

        {/* Pack dropdown */}
        <Menu anchorEl={packMenuAnchor} open={Boolean(packMenuAnchor)} onClose={() => setPackMenuAnchor(null)}>
            {packOptions.map(option => {
                const label = packOptionLabels.find(l => packOptionLabelMap[l]?.pack.id === option.pack.id)
                return (
                    <MenuItem key={option.pack.id} disabled={option.disabled} onClick={handleQuickPack(option.pack)}>
                        <ListItemIcon><Widgets fontSize="small"/></ListItemIcon>
                        {label}
                    </MenuItem>
                )
            })}
        </Menu>

        {/* Add payment dropdown */}
        <Menu anchorEl={addPaymentAnchor} open={Boolean(addPaymentAnchor)} onClose={() => setAddPaymentAnchor(null)}>
            {generalSettings.paymentMethods.map((method) => (
                <MenuItem key={method} onClick={() => handleQuickPayment(method)}>
                    <PaymentLabel paymentType={method}/>
                </MenuItem>
            ))}
            <MenuItem onClick={() => {
                setAddPaymentAnchor(null)
                withRegisterCheck(() => setPaymentModal(true))
            }}>
                {t(TK.advanced)}...
            </MenuItem>
        </Menu>

        {/* Request payment dropdown */}
        <Menu anchorEl={requestMenuAnchor} open={Boolean(requestMenuAnchor)} onClose={() => setRequestMenuAnchor(null)}>
            <MenuItem onClick={() => { setRequestMenuAnchor(null); setMbwayModal(true); }}>
                <ListItemIcon><img style={{height: '20px'}} src='/payment-methods/MBWAY.svg'/></ListItemIcon>
                {t(TK.sendMbWayRequestToClient)}
            </MenuItem>
            <MenuItem onClick={() => { setRequestMenuAnchor(null); handleAutomaticPayment('mb')(); }}>
                <ListItemIcon><img style={{height: '20px'}} src='/payment-methods/MULTIBANCO.svg'/></ListItemIcon>
                {t(TK.sendMbRequestToClient)}
            </MenuItem>
            <MenuItem onClick={() => { setRequestMenuAnchor(null); handleSendCheckoutEmail(); }}>
                <ListItemIcon><ShoppingCartCheckoutIcon fontSize="small"/></ListItemIcon>
                {t(TK.sendCheckoutLinkToClientEmail)}
            </MenuItem>
        </Menu>

        <Modal isOpen={registerModalOpen} toggle={() => setRegisterModalOpen(false)}>
            <ModalHeader toggle={() => setRegisterModalOpen(false)}>{t(TK.cashRegisterSelectRegister)}</ModalHeader>
            <ModalBody style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacer-S)' }}>
                <AppButton color="success" onClick={() => handleRegisterSelect('')}>
                    {t(TK.cashRegisterOwnRegister)}
                </AppButton>
                {sharedRegisterNames.map(name => (
                    <AppButton key={name} color="success" onClick={() => handleRegisterSelect(name)}>
                        {name}
                    </AppButton>
                ))}
            </ModalBody>
        </Modal>

        <Modal isOpen={mbwayModal} toggle={() => setMbwayModal(!mbwayModal)}>
            <ModalHeader toggle={() => setMbwayModal(!mbwayModal)}>{t(TK.areYouSure)}</ModalHeader>
            <ModalBody>{t(TK.mbwayWarningMessage)}</ModalBody>
            <ModalFooter>
                <AppButton id="appointment-confirm-mbway-button" color="success" onClick={handleAutomaticPayment('mbw')}>{t(TK.send)}</AppButton>{' '}
                <AppButton id="appointment-abort-mbway-button" color="secondary" onClick={() => setMbwayModal(false)}>{t(TK.cancel)}</AppButton>
            </ModalFooter>
        </Modal>

        <Modal isOpen={paymentModal} toggle={() => setPaymentModal(false)} size="lg">
            <ModalHeader toggle={() => setPaymentModal(false)}>{t(TK.add)} {t(TK.payment)}</ModalHeader>
            <ModalBody>
                <div style={{display: 'flex', flexDirection: 'column', gap: 'var(--spacer-M)'}}>
                    {showPacks &&
                        <div>
                            <AutocompleteTextField
                                id="appointment-pack-modal"
                                label={`${t(TK.pack)}/${t(TK.subscription)}`}
                                options={packOptionLabels}
                                value={packOptionLabels.find(label => 'packId' in entity && packOptionLabelMap[label]?.pack.id === entity.packId)}
                                onValueChanged={onPackChanged}
                                onTextChanged={onPackChanged}
                                onAddNewOption={onAddManualPack}
                                addNewLabel={t(TK.search)}
                                isOptionDisabled={option => packOptionLabelMap[option]?.disabled ?? false}
                            />
                            {isAdmin && packData && packSettings && (
                                <Typography size='smaller'>
                                    {t(TK.noteAboutRealPriceWithPack)} <b>{formatEuros(getActualPriceWithPack(entity as AppointmentData, packData, packSettings))}</b>
                                </Typography>
                            )}
                        </div>
                    }

                    {!packIsMandatory && !('packId' in entity && entity.packId) && (
                        <div>
                            <MultiplePaymentsField
                                id="appointment-payments-modal"
                                label={t(TK.payment)}
                                values={entity.payments || []}
                                maxValue={totalToPay}
                                cashRegisterName={selectedCashRegisterName || undefined}
                                onChange={onPaymentsChanged}
                            />
                        </div>
                    )}
                </div>
            </ModalBody>

            <ModalFooter>
                <AppButton id="appointment-payment-cancel-button" color="secondary" onClick={() => setPaymentModal(false)}>{t(TK.close)}</AppButton>
            </ModalFooter>
        </Modal>

    </>)
}
